import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Icon, Switch } from '@repo/ui';
import {
  CLIO_REPLICATION_REPORT_KINDS,
  type ClioReplicationReportKind,
  type ClioReplicationSchedulerEntityConfig,
  type ClioReplicationSchedulerConfig,
  type ClioReplicationSchedulerEntity,
  type ReplicateToDWConditionType,
} from '@xtnd/web/shared/types/tenantSettingTypes';
import {
  CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS,
  normalizeClioReplicationSchedulerConfig,
} from '@xtnd/web/features/settings/utils';
import {
  useDropEntityTablesMutation,
  useDropReportTableMutation,
  useResyncReportMonthMutation,
  useResyncSnapshotReportMutation,
  useInvokeDwSyncMutation,
} from '@xtnd/web/features/settings/queries';

interface DWEnablementConfigurationCardProps {
  config?: Partial<ClioReplicationSchedulerConfig> | null;
  onSave: (config: ClioReplicationSchedulerConfig) => Promise<void>;
  isSaving?: boolean;
}

const DEFAULT_DW_WINDOW_TYPE: ReplicateToDWConditionType = 'rollingMonthsWindow';
const DEFAULT_DW_ROLLING_MONTHS = 12;

type ConfirmDropState =
  | { type: 'entity'; entityKey: string }
  | { type: 'report'; reportKind: string }
  | null;

type ConfirmExportByMonthState = {
  reportKind: ClioReplicationReportKind;
  newValue: boolean;
} | null;

export function DWEnablementConfigurationCard({
  config,
  onSave,
  isSaving = false,
}: DWEnablementConfigurationCardProps) {
  const normalizedInitialConfig = useMemo(
    () => normalizeClioReplicationSchedulerConfig(config),
    [config]
  );

  const [draftConfig, setDraftConfig] = useState<ClioReplicationSchedulerConfig>(normalizedInitialConfig);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState<ConfirmDropState>(null);
  const [selectedMonths, setSelectedMonths] = useState<Record<string, string>>({});
  const [confirmExportByMonth, setConfirmExportByMonth] = useState<ConfirmExportByMonthState>(null);
  const [confirmSnapshotResync, setConfirmSnapshotResync] = useState<string | null>(null);
  const [syncPrompt, setSyncPrompt] = useState<{ type: 'all' | 'entities' | 'reports' } | null>(null);

  useEffect(() => {
    setDraftConfig(normalizedInitialConfig);
    setConfirmDrop(null);
  }, [normalizedInitialConfig]);

  const isDirty = useMemo(
    () => JSON.stringify(draftConfig) !== JSON.stringify(normalizedInitialConfig),
    [draftConfig, normalizedInitialConfig]
  );

  const dropEntityMutation = useDropEntityTablesMutation();
  const dropReportMutation = useDropReportTableMutation();
  const resyncMutation = useResyncReportMonthMutation();
  const resyncSnapshotMutation = useResyncSnapshotReportMutation();
  const invokeSyncMutation = useInvokeDwSyncMutation();

  const updateDwEntity = (entity: ClioReplicationSchedulerEntity, enabled: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      entities: {
        ...prev.entities,
        [entity]: {
          ...prev.entities[entity],
          replicateToDW: enabled,
          ...(enabled
            ? buildDefaultDwWindow(prev.entities[entity])
            : {}),
        },
      },
    }));
  };

  const updateDwEntityWindowType = (
    entity: ClioReplicationSchedulerEntity,
    conditionType: ReplicateToDWConditionType
  ) => {
    setDraftConfig(prev => {
      const current = prev.entities[entity];
      const fallbackStartDate = rollingMonthsAgoToDateInput(DEFAULT_DW_ROLLING_MONTHS);
      const nextValue =
        conditionType === 'startDate'
          ? typeof current?.replicateToDWValue === 'string'
            ? current.replicateToDWValue
            : fallbackStartDate
          : typeof current?.replicateToDWValue === 'number'
            ? current.replicateToDWValue
            : DEFAULT_DW_ROLLING_MONTHS;

      return {
        ...prev,
        entities: {
          ...prev.entities,
          [entity]: {
            ...current,
            replicateToDWConditionType: conditionType,
            replicateToDWValue: nextValue,
          },
        },
      };
    });
  };

  const updateDwEntityWindowValue = (
    entity: ClioReplicationSchedulerEntity,
    value: string | number
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      entities: {
        ...prev.entities,
        [entity]: {
          ...prev.entities[entity],
          replicateToDWValue: value,
        },
      },
    }));
  };

  const updateReportReplication = (
    patch: Partial<ClioReplicationSchedulerConfig['reportReplication']>
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      reportReplication: {
        ...prev.reportReplication,
        ...patch,
      },
    }));
  };

  const updateReportEnabled = (reportKind: ClioReplicationReportKind, enabled: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      reportReplication: {
        ...prev.reportReplication,
        reports: {
          ...prev.reportReplication.reports,
          [reportKind]: enabled,
        },
      },
    }));
  };

  const updateReportExportByMonth = (reportKind: ClioReplicationReportKind, byMonth: boolean) => {
    setDraftConfig(prev => ({
      ...prev,
      reportReplication: {
        ...prev.reportReplication,
        reportExportByMonth: {
          ...prev.reportReplication.reportExportByMonth,
          [reportKind]: byMonth,
        },
      },
    }));
  };

  const handleExportByMonthToggle = (reportKind: ClioReplicationReportKind, byMonth: boolean) => {
    const currentValue = draftConfig.reportReplication.reportExportByMonth?.[reportKind] !== false;
    if (byMonth === currentValue) return;
    setConfirmExportByMonth({ reportKind, newValue: byMonth });
  };

  const handleSave = async () => {
    if (!isDirty || isSaving || isSubmitting) return;

    const previousConfig = normalizedInitialConfig;

    try {
      setIsSubmitting(true);
      await onSave(draftConfig);

      // After successful save, detect what changed and prompt for sync
      const syncType = detectSyncType(previousConfig, draftConfig);
      if (syncType) {
        setSyncPrompt({ type: syncType });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabled = isSaving || isSubmitting;
  const isOperationRunning =
    dropEntityMutation.isPending || dropReportMutation.isPending ||
    resyncMutation.isPending || resyncSnapshotMutation.isPending || invokeSyncMutation.isPending;

  const rollingMonthOptions = useMemo(
    () => generateRollingMonths(normalizedInitialConfig.reportReplication.backfillDefaultPeriods ?? 12),
    [normalizedInitialConfig.reportReplication.backfillDefaultPeriods]
  );

  return (
    <Card padding="lg" variant="outlined">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Icon name="HiChartBar" className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-xtnd-dark-800 dark:text-white">
                DW and Redshift Configuration
              </h3>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Configure DW extraction, Redshift enablement, and fiscal or calendar report periods.
              </p>
            </div>
          </div>
          <Badge variant={draftConfig.reportReplication.enabled ? 'success' : 'warning'}>
            {draftConfig.reportReplication.enabled ? 'Report Extraction Enabled' : 'Report Extraction Disabled'}
          </Badge>
        </div>
      </CardHeader>

      <CardBody className="space-y-5">
        <div className="space-y-3">
          <p className="text-sm font-semibold text-xtnd-dark-800 dark:text-white">XTND to Data Warehouse Configuration</p>
          {CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS.map(entity => {
            const entityConfig = draftConfig.entities[entity.key];
            const savedEntityConfig = normalizedInitialConfig.entities[entity.key];
            const dwWindowType: ReplicateToDWConditionType =
              entityConfig?.replicateToDWConditionType === 'startDate'
                ? 'startDate'
                : DEFAULT_DW_WINDOW_TYPE;

            const isSavedDisabled = savedEntityConfig?.replicateToDW === false;
            const showDropButton = isSavedDisabled && !isDirty;
            const isConfirmingDrop =
              confirmDrop?.type === 'entity' && confirmDrop.entityKey === entity.key;

            return (
              <div
                key={entity.key}
                className="space-y-3 rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-3"
              >
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">{entity.label}</p>
                    <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Enable this entity for DW/Redshift replication.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {showDropButton && !isConfirmingDrop && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmDrop({ type: 'entity', entityKey: entity.key })}
                        disabled={isOperationRunning}
                      >
                        Drop Table
                      </Button>
                    )}
                    <Switch
                      checked={entityConfig?.replicateToDW === true}
                      onChange={checked => updateDwEntity(entity.key, checked)}
                      disabled={disabled}
                      aria-label={`Toggle ${entity.label} DW replication`}
                    />
                  </div>
                </div>

                {isConfirmingDrop && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-3 space-y-2">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                      This will permanently drop all Redshift tables for this entity. This cannot be undone.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        loading={dropEntityMutation.isPending}
                        onClick={async () => {
                          await dropEntityMutation.mutateAsync(entity.key);
                          setConfirmDrop(null);
                        }}
                      >
                        Proceed
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDrop(null)}
                        disabled={dropEntityMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                <div className={`${entityConfig?.replicateToDW ? '' : 'opacity-60'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                        Data Window Type
                      </label>
                      <select
                        value={dwWindowType}
                        onChange={event =>
                          updateDwEntityWindowType(
                            entity.key,
                            event.target.value as ReplicateToDWConditionType
                          )
                        }
                        disabled={disabled || entityConfig?.replicateToDW !== true}
                        className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                      >
                        <option value="rollingMonthsWindow">Rolling Months Window</option>
                        <option value="startDate">Start Date</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                        Data Window Value
                      </label>
                      {dwWindowType === 'rollingMonthsWindow' ? (
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={toRollingMonthsValue(entityConfig?.replicateToDWValue)}
                          onChange={event =>
                            updateDwEntityWindowValue(
                              entity.key,
                              toRollingMonthsValue(event.target.value)
                            )
                          }
                          disabled={disabled || entityConfig?.replicateToDW !== true}
                          className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                        />
                      ) : (
                        <input
                          type="date"
                          value={toDateInputValue(entityConfig?.replicateToDWValue)}
                          onChange={event =>
                            updateDwEntityWindowValue(entity.key, event.target.value)
                          }
                          disabled={disabled || entityConfig?.replicateToDW !== true}
                          className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-3 rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-xtnd-dark-800 dark:text-white">Clio Report to DW Extraction</p>
              <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                Master toggle for Clio reporting extracts into DW/Redshift.
              </p>
            </div>
            <Switch
              checked={draftConfig.reportReplication.enabled}
              onChange={checked => updateReportReplication({ enabled: checked })}
              disabled={disabled}
              aria-label="Toggle Clio report DW extraction"
            />
          </div>

          <div className={`space-y-3 ${!draftConfig.reportReplication.enabled ? 'opacity-60' : ''}`}>
            <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
              Period Model
            </label>
            <select
              value={draftConfig.reportReplication.periodModel}
              onChange={event =>
                updateReportReplication({
                  periodModel: event.target.value as 'CALENDAR' | 'FISCAL',
                })
              }
              disabled={disabled || !draftConfig.reportReplication.enabled}
              className="w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
            >
              <option value="CALENDAR">Calendar Month</option>
              <option value="FISCAL">Fiscal 52/53 Week</option>
            </select>

            {draftConfig.reportReplication.periodModel === 'FISCAL' && (
              <>
                <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                  Fiscal Pattern
                </label>
                <select
                  value={draftConfig.reportReplication.fiscalPattern}
                  onChange={event =>
                    updateReportReplication({
                      fiscalPattern: event.target.value as '4-4-5' | '4-5-4' | '5-4-4',
                    })
                  }
                  disabled={disabled || !draftConfig.reportReplication.enabled}
                  className="w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value="4-4-5">4-4-5</option>
                  <option value="4-5-4">4-5-4</option>
                  <option value="5-4-4">5-4-4</option>
                </select>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                  Week Start Day
                </label>
                <select
                  value={draftConfig.reportReplication.weekStartDay}
                  onChange={event =>
                    updateReportReplication({
                      weekStartDay: event.target.value as 'MONDAY' | 'SUNDAY',
                    })
                  }
                  disabled={disabled || !draftConfig.reportReplication.enabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value="MONDAY">Monday</option>
                  <option value="SUNDAY">Sunday</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                  Fiscal Year Anchor Date
                </label>
                <input
                  type="date"
                  value={draftConfig.reportReplication.fiscalYearAnchorDate}
                  onChange={event =>
                    updateReportReplication({
                      fiscalYearAnchorDate: event.target.value,
                    })
                  }
                  disabled={disabled || !draftConfig.reportReplication.enabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                  Week 53 Handling
                </label>
                <select
                  value={draftConfig.reportReplication.week53Handling}
                  onChange={event =>
                    updateReportReplication({
                      week53Handling: event.target.value as 'P12' | 'P11' | 'nearest-end',
                    })
                  }
                  disabled={disabled || !draftConfig.reportReplication.enabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value="P12">Period 12</option>
                  <option value="P11">Period 11</option>
                  <option value="nearest-end">Nearest End</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-200">
                  Default Backfill Periods
                </label>
                <select
                  value={draftConfig.reportReplication.backfillDefaultPeriods}
                  onChange={event =>
                    updateReportReplication({
                      backfillDefaultPeriods: Number(event.target.value) as 12 | 24 | 36,
                    })
                  }
                  disabled={disabled || !draftConfig.reportReplication.enabled}
                  className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm text-xtnd-dark-800 dark:text-white"
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={36}>36</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-3">
              <div>
                <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">
                  Finalize Closed Periods
                </p>
                <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                  Enables close-period FULL snapshots at period boundaries.
                </p>
              </div>
              <Switch
                checked={draftConfig.reportReplication.finalizeClosedPeriods}
                onChange={checked => updateReportReplication({ finalizeClosedPeriods: checked })}
                disabled={disabled || !draftConfig.reportReplication.enabled}
                aria-label="Toggle closed period finalization"
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">
                Replicated Reports
              </p>
              {CLIO_REPLICATION_REPORT_KINDS.map(reportKind => {
                const isSavedReportDisabled =
                  normalizedInitialConfig.reportReplication.reports[reportKind] === false;
                const showReportDropButton = isSavedReportDisabled && !isDirty;
                const isConfirmingReportDrop =
                  confirmDrop?.type === 'report' && confirmDrop.reportKind === reportKind;
                const selectedMonth = selectedMonths[reportKind] ?? '';

                return (
                  <div
                    key={reportKind}
                    className="rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-2 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-dark-100">{reportKind}</p>
                      <div className="flex items-center gap-2">
                        {showReportDropButton && !isConfirmingReportDrop && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setConfirmDrop({ type: 'report', reportKind })}
                            disabled={isOperationRunning}
                          >
                            Drop Table
                          </Button>
                        )}
                        <Switch
                          checked={draftConfig.reportReplication.reports[reportKind] !== false}
                          onChange={checked => updateReportEnabled(reportKind, checked)}
                          disabled={disabled || !draftConfig.reportReplication.enabled}
                          aria-label={`Toggle ${reportKind} report replication`}
                        />
                      </div>
                    </div>

                    {isConfirmingReportDrop && (
                      <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-3 space-y-2">
                        <p className="text-sm font-medium text-red-700 dark:text-red-300">
                          This will permanently drop the <code className="font-mono">clio_report_{reportKind}</code> table. This cannot be undone.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            loading={dropReportMutation.isPending}
                            onClick={async () => {
                              await dropReportMutation.mutateAsync(reportKind);
                              setConfirmDrop(null);
                            }}
                          >
                            Proceed
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmDrop(null)}
                            disabled={dropReportMutation.isPending}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {draftConfig.reportReplication.reports[reportKind] !== false && (
                      <div className="flex items-center justify-between pt-1">
                        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                          Export By Month
                        </p>
                        <div className="flex items-center gap-2">
                          <Badge variant={draftConfig.reportReplication.reportExportByMonth?.[reportKind] !== false ? 'info' : 'warning'}>
                            {draftConfig.reportReplication.reportExportByMonth?.[reportKind] !== false ? 'Monthly' : 'Snapshot'}
                          </Badge>
                          <Switch
                            checked={draftConfig.reportReplication.reportExportByMonth?.[reportKind] !== false}
                            onChange={checked => handleExportByMonthToggle(reportKind, checked)}
                            disabled={disabled || !draftConfig.reportReplication.enabled}
                            aria-label={`Toggle ${reportKind} export by month`}
                          />
                        </div>
                      </div>
                    )}

                    {confirmExportByMonth?.reportKind === reportKind && (
                      <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 space-y-2">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                          {confirmExportByMonth.newValue
                            ? 'Switching to Monthly mode will begin accumulating data by month. Any existing snapshot rows (with no month code) will be automatically purged on the next sync.'
                            : 'Switching to Snapshot mode will replace all accumulated monthly data with a single point-in-time snapshot on the next sync. Historical month-by-month data will be lost.'}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant={confirmExportByMonth.newValue ? 'primary' : 'destructive'}
                            size="sm"
                            onClick={() => {
                              updateReportExportByMonth(confirmExportByMonth.reportKind, confirmExportByMonth.newValue);
                              setConfirmExportByMonth(null);
                            }}
                          >
                            Confirm
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmExportByMonth(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {normalizedInitialConfig.reportReplication.enabled && (
                      draftConfig.reportReplication.reportExportByMonth?.[reportKind] !== false ? (
                        <div className="flex items-center gap-2 pt-1">
                          <select
                            value={selectedMonth}
                            onChange={e =>
                              setSelectedMonths(prev => ({ ...prev, [reportKind]: e.target.value }))
                            }
                            disabled={isOperationRunning}
                            className="flex-1 rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-500 bg-white dark:bg-xtnd-dark-700 px-2 py-1 text-xs text-xtnd-dark-800 dark:text-white"
                          >
                            <option value="">Select month to resync…</option>
                            {rollingMonthOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!selectedMonth || isOperationRunning}
                            loading={resyncMutation.isPending}
                            onClick={() => {
                              if (!selectedMonth) return;
                              resyncMutation.mutate({ reportKind, monthCode: selectedMonth });
                            }}
                          >
                            Resync
                          </Button>
                        </div>
                      ) : (
                        <div className="pt-1 space-y-2">
                          {confirmSnapshotResync === reportKind ? (
                            <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 space-y-2">
                              <p className="text-sm text-amber-700 dark:text-amber-300">
                                This will replace all data in <strong>{reportKind.replace(/_/g, ' ')}</strong> with a fresh snapshot from Clio. This may take several minutes.
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  loading={resyncSnapshotMutation.isPending}
                                  disabled={isOperationRunning && !resyncSnapshotMutation.isPending}
                                  onClick={() => {
                                    resyncSnapshotMutation.mutate({ reportKind }, {
                                      onSettled: () => setConfirmSnapshotResync(null),
                                    });
                                  }}
                                >
                                  Confirm Resync
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setConfirmSnapshotResync(null)}
                                  disabled={resyncSnapshotMutation.isPending}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isOperationRunning}
                              onClick={() => setConfirmSnapshotResync(reportKind)}
                            >
                              Resync Snapshot
                            </Button>
                          )}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {syncPrompt && (
          <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-3 space-y-2">
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Configuration saved. Some changes may require a DW sync to take effect.
            </p>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                loading={invokeSyncMutation.isPending}
                disabled={isOperationRunning}
                onClick={() => {
                  invokeSyncMutation.mutate(syncPrompt.type, {
                    onSuccess: () => setSyncPrompt(null),
                  });
                }}
              >
                Invoke DW Sync
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSyncPrompt(null)}
                disabled={invokeSyncMutation.isPending}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => invokeSyncMutation.mutate('all')}
            disabled={isDirty || isOperationRunning || disabled}
            loading={invokeSyncMutation.isPending}
          >
            Invoke DW Sync
          </Button>
          <Button onClick={handleSave} disabled={!isDirty || disabled} loading={disabled}>
            Save DW and Redshift Configuration
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function buildDefaultDwWindow(entityConfig?: ClioReplicationSchedulerEntityConfig) {
  const hasWindowType =
    entityConfig?.replicateToDWConditionType === 'rollingMonthsWindow' ||
    entityConfig?.replicateToDWConditionType === 'startDate';
  const hasWindowValue = entityConfig?.replicateToDWValue !== undefined;

  if (hasWindowType && hasWindowValue) {
    return {};
  }

  return {
    replicateToDWConditionType: DEFAULT_DW_WINDOW_TYPE,
    replicateToDWValue: DEFAULT_DW_ROLLING_MONTHS,
  };
}

function toRollingMonthsValue(value: unknown): number {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : Number.NaN;

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_DW_ROLLING_MONTHS;
  }

  return Math.floor(parsed);
}

function toDateInputValue(value: unknown): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  return rollingMonthsAgoToDateInput(DEFAULT_DW_ROLLING_MONTHS);
}

function rollingMonthsAgoToDateInput(months: number): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().slice(0, 10);
}

function generateRollingMonths(n: number): Array<{ value: string; label: string }> {
  const months: Array<{ value: string; label: string }> = [];
  const now = new Date();
  // Start from previous complete month
  const startMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  for (let i = 0; i < n; i++) {
    const d = new Date(startMonth.getFullYear(), startMonth.getMonth() - i, 1);
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    months.push({ value: `${yyyy}${mm}`, label: `${yyyy}-${mm}` });
  }

  return months;
}

/**
 * Compare previous and new config to determine which ETL type to invoke.
 * Returns null if no sync-worthy changes detected.
 */
function detectSyncType(
  prev: ClioReplicationSchedulerConfig,
  next: ClioReplicationSchedulerConfig
): 'all' | 'entities' | 'reports' | null {
  let entityChanged = false;
  let reportChanged = false;

  // Check entity-level changes
  for (const entity of CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS) {
    const prevEntity = prev.entities[entity.key];
    const nextEntity = next.entities[entity.key];

    if (prevEntity?.replicateToDW !== nextEntity?.replicateToDW) {
      entityChanged = true;
      break;
    }

    if (
      nextEntity?.replicateToDW &&
      (prevEntity?.replicateToDWConditionType !== nextEntity?.replicateToDWConditionType ||
        String(prevEntity?.replicateToDWValue) !== String(nextEntity?.replicateToDWValue))
    ) {
      entityChanged = true;
      break;
    }
  }

  // Check report-level changes
  if (prev.reportReplication.enabled !== next.reportReplication.enabled) {
    reportChanged = true;
  }

  if (
    prev.reportReplication.periodModel !== next.reportReplication.periodModel ||
    prev.reportReplication.fiscalPattern !== next.reportReplication.fiscalPattern ||
    prev.reportReplication.weekStartDay !== next.reportReplication.weekStartDay ||
    prev.reportReplication.fiscalYearAnchorDate !== next.reportReplication.fiscalYearAnchorDate ||
    prev.reportReplication.week53Handling !== next.reportReplication.week53Handling ||
    prev.reportReplication.backfillDefaultPeriods !== next.reportReplication.backfillDefaultPeriods
  ) {
    reportChanged = true;
  }

  if (!reportChanged) {
    for (const reportKind of CLIO_REPLICATION_REPORT_KINDS) {
      if (prev.reportReplication.reports[reportKind] !== next.reportReplication.reports[reportKind]) {
        reportChanged = true;
        break;
      }

      const prevByMonth = prev.reportReplication.reportExportByMonth?.[reportKind] !== false;
      const nextByMonth = next.reportReplication.reportExportByMonth?.[reportKind] !== false;
      if (prevByMonth !== nextByMonth) {
        reportChanged = true;
        break;
      }
    }
  }

  if (entityChanged && reportChanged) return 'all';
  if (entityChanged) return 'entities';
  if (reportChanged) return 'reports';
  return null;
}
